import { api } from './api';

export interface Company {
  id: string;
  name: string;
  countryCode: string;
  fiscalYearStartMonth: number;
  reportingCurrency: string;
  baseUnit: '1' | '1000' | '1000000';
  primaryLocale: string;
  plan: 'free' | 'growth' | 'global' | 'enterprise';
  seatLimit: number;
  verificationStatus: 'pending' | 'verified' | 'pending_review' | 'rejected';
  aiMonthlyCapCents: number | null;
  industry: string | null;
  targetRegions: string[];
  createdAt: string;
}

export interface CreateCompanyDto {
  name: string;
  countryCode: string;
  fiscalYearStartMonth: number;
  reportingCurrency: string;
  baseUnit: '1' | '1000' | '1000000';
  primaryLocale: string;
  companyUrl?: string | null;
  industry?: string | null;
  targetRegions?: string[];
}

export interface SeatUsage {
  used: number;
  /** -1 means unlimited (enterprise tier). Otherwise a positive integer cap. */
  limit: number;
  plan: 'free' | 'growth' | 'global' | 'enterprise';
}

export interface CompanyMember {
  id: string;
  email: string;
  displayName: string;
  role: 'owner' | 'admin' | 'editor' | 'viewer' | 'member';
  avatarUrl: string | null;
  primaryLocale: string | null;
}

export const companiesApi = {
  async list(): Promise<Company[]> {
    const r = await api.get<Company[]>('/api/companies');
    return r.data;
  },
  async create(dto: CreateCompanyDto): Promise<{ company: Company; verificationReason: string; defaultWorkspaceId: string }> {
    const r = await api.post<{ company: Company; verificationReason: string; defaultWorkspaceId: string }>('/api/companies', dto);
    return r.data;
  },
  async seatUsage(companyId: string): Promise<SeatUsage> {
    const r = await api.get<SeatUsage>(`/api/companies/${companyId}/seat-usage`);
    return r.data;
  },
  async members(companyId: string): Promise<CompanyMember[]> {
    const r = await api.get<{ items: CompanyMember[]; total: number }>(
      `/api/companies/${companyId}/members`,
    );
    return r.data.items;
  },
};

/** Default fiscal year start month by country (most JP companies = April). */
export function defaultFiscalStartMonthForCountry(country: string | null | undefined): number {
  if (!country) return 1;
  const c = country.toUpperCase();
  if (c === 'JP') return 4;
  if (c === 'IN') return 4;
  if (c === 'UK' || c === 'GB') return 4;
  return 1;
}
